<template>
    <v-card :loading="loading">
        <v-card-title>
            <v-btn
                class="mr-3"
                depressed
                color="primary"
                icon
                rounded
                :loading="loading"
                @click="close"
            >
                <v-icon>mdi-close</v-icon>
            </v-btn>
            Upload File
            <v-spacer></v-spacer>
        </v-card-title>
        <v-divider class="ml-4 mr-5" />
        <v-card-text>
            <v-form v-model="valid" class="mt-4">
                <v-row no-gutters>
                    <v-text-field
                        v-model="trm"
                        type="number"
                        label="TRM *"
                        class="my-2"
                        prepend-icon="mdi-currency-usd"
                        :rules="[rules.required]"
                        required
                    >
                    </v-text-field>
                </v-row>
                <v-row no-gutters>
                    <v-col cols="12">
                        <v-menu
                            ref="menu"
                            v-model="menu"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="cutOffDate"
                                    label="Cut-off Date *"
                                    prepend-icon="mdi-calendar"
                                    :rules="[rules.required]"
                                    readonly
                                    v-bind="attrs"
                                    class="my-2"
                                    v-on="on"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="cutOffDate"
                                @input="menu = false"
                            >
                            </v-date-picker>
                        </v-menu>
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-file-input
                        truncate-length="15"
                        v-model="file"
                        placeholder="File *"
                        accept=".xlsx"
                        hide-details
                        required
                        class="my-2"
                        :rules="[rules.required]"
                    ></v-file-input>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-spacer />
            <v-btn
                color="primary"
                rounded
                :loading="loading"
                class="mb-3"
                @click="readXLSX"
                :disabled="!valid"
            >
                IMPORT
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { mapMutations } from 'vuex'
import * as XLSX from 'xlsx/xlsx.mjs'
import API from '@/services/api'
import { storage } from '@/services/firebase'
const { v4: uuidv4 } = require('uuid')

export default {
    name: 'UploadPOC',
    props: {
        settingExchange: {
            type: Object,
            default() {
                return {}
            },
        },
        oldData: {
            type: Array,
            default() {
                return []
            },
        },
    },
    data: () => ({
        loading: false,
        valid: false,
        file: null,
        rules: {
            required: v => !!v || 'invalid value',
        },
        trm: 4300,
        estimatedMargin: 0,
        selectedYear: '',
        selectedMonth: '',
        cutOffDate: null,
        menu: false,
        sheetData: [],
        companyId: JSON.parse(localStorage.getItem('company')),
    }),
    async mounted() {
        const currentDate = new Date()
        this.selectedYear = currentDate.getFullYear().toString()
        this.selectedMonth = (currentDate.getMonth() + 1)
            .toString()
            .padStart(2, '0')
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async readXLSX() {
            try {
                this.loading = true
                if (
                    this.file.name.endsWith('.xls') ||
                    this.file.name.endsWith('.xlsx')
                ) {
                    const allowedKeys = [
                        'CO',
                        'Costs',
                        'Nómina',
                        'Billed to Date',
                    ]
                    const data = await this.file.arrayBuffer()
                    const workbook = XLSX.read(data)
                    const sheetName = workbook.SheetNames[0]
                    const headerRow =
                        XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
                            header: 1,
                            range: 0,
                        })[0] || []
                    const missingColumns = allowedKeys.filter(
                        key => !headerRow.includes(key)
                    )
                    if (missingColumns.length > 0) {
                        throw new Error(
                            'The file does not contain the required columns'
                        )
                    }
                    const xlsData = XLSX.utils.sheet_to_json(
                        workbook.Sheets[sheetName],
                        {
                            header: allowedKeys,
                            raw: false,
                            defval: null,
                            range: 1,
                        }
                    )
                    this.sheetData = xlsData.map(row => ({
                        orderCode: row.CO,
                        costs: this.parseCurrency(row.Costs),
                        payroll: this.parseCurrency(row['Nómina']),
                        b2d: this.parseCurrency(row['Billed to Date']),
                        id: uuidv4(),
                        trm: parseFloat(this.trm),
                        year: this.selectedYear,
                        month: this.selectedMonth,
                        cutOffDate: this.cutOffDate,
                    }))
                    this.sheetData = Array.from(
                        new Map(
                            this.sheetData.map(i => [i.orderCode, i])
                        ).values()
                    )
                    const projects = await API.getProjectsBySapCode({
                        codes: this.sheetData.map(item => item['orderCode']),
                    })
                    this.sheetData = this.sheetData.filter(obj => {
                        const project = projects.find(
                            p => p.sapCode === obj.orderCode
                        )
                        if (!project) {
                            return false
                        }
                        obj.price = project.price
                        obj.b2b = project.b2b
                        obj.currency = project.currency
                        if (project.currency == 'USD') {
                            obj.b2b = obj.b2b * obj.trm
                            obj.price = obj.price * obj.trm
                        } else if (project.currency != 'COP') {
                            const exchange = this.settingExchange.exchanges.find(
                                u => u.currency === project.currency
                            )
                            if (exchange) {
                                obj.b2b = obj.b2b * parseFloat(exchange.money)
                                obj.price =
                                    obj.price * parseFloat(exchange.money)
                            }
                        }
                        obj.totalAmountBeforeTaxes = obj.price
                        obj.job = project.name
                        obj.client = project.clientData
                            ? project.clientData.name
                            : ''
                        obj.reference = project.reference
                        obj.projectId = project.id
                        const oldData = this.oldData.find(
                            p => String(p.orderCode) === obj.orderCode
                        )
                        if (oldData) {
                            obj.estimatedMargin = parseFloat(
                                oldData.estimatedMargin
                            )
                        } else {
                            obj.estimatedMargin = 0.25
                        }
                        obj.estimatedRevenues = obj.b2d + obj.b2b
                        obj.estimatedGrossProfit =
                            obj.estimatedRevenues * obj.estimatedMargin
                        obj.estimatedCost = Math.abs(
                            obj.estimatedRevenues - obj.estimatedGrossProfit
                        )
                        obj.costOfRevenues =
                            (obj.costs || 0) + (obj.payroll || 0)
                        obj.percentageCompleted =
                            obj.costOfRevenues / obj.estimatedCost
                        obj.revenuesEarned =
                            obj.percentageCompleted * obj.estimatedRevenues
                        obj.grossProfit =
                            obj.revenuesEarned - obj.costOfRevenues
                        obj.billedToDateForThisMonth =
                            obj.totalAmountBeforeTaxes - obj.b2b
                        obj.estimatedCostToComplete =
                            obj.estimatedCost - obj.costOfRevenues
                        return true
                    })
                    const blob = new Blob([JSON.stringify(this.sheetData)], {
                        type: 'application/json',
                    })
                    const path = `${this.companyId}/POC/POCData.json`
                    const storageRef = storage().ref(path)
                    await storageRef.put(blob)
                    this.$emit('addPOC', this.sheetData)
                } else {
                    throw new Error('Unsupported file format')
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.close()
            }
        },
        parseCurrency(value) {
            if (typeof value !== 'string') return parseFloat(value) || null
            const cleaned = value.replace(/[^\d.-]/g, '').replace(/,/g, '')
            return parseFloat(cleaned) || null
        },
        close() {
            this.$emit('close')
        },
    },
}
</script>

<style></style>
